import { lazy, Suspense } from "react";
import { ChunkErrorBoundary } from "app/chunk-error-boundary";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { NotFoundPage, Spinner } from "dash-ui";
import { RoutesPath } from "flow-components/routes/routes";
import ProtectedRout from "flow-components/routes/ProtectedRout/ProtectedRout";
import useInitViewer from "common/hooks/useInitViewer/useInitViewer";
import { useBrowserTabTitle } from "common/hooks/use-browser-tab-title/use-browser-tab-title";

// Every route is code-split: the entry bundle carries the shell, not all 27 pages.
const SignIn = lazy(() => import("pages/authentication/SignInPage/SignInPage"));
const ForgotPasswordPage = lazy(
  () => import("pages/authentication/ForgotPasswordPage/ForgotPasswordPage")
);
const ResetPasswordPage = lazy(
  () => import("pages/authentication/ResetPasswordPage/ResetPasswordPage")
);
const OrganizationsPage = lazy(
  () => import("pages/organizations/OrganizationsPage/OrganizationsPage")
);
const OrganizationDetailsPage = lazy(
  () =>
    import(
      "pages/organizations/OrganizationDetailsPage/OrganizationDetailsPage"
    )
);
const PropertiesPage = lazy(
  () => import("pages/properties/PropertiesPage/PropertiesPage")
);
const PropertyDetailsPage = lazy(
  () => import("pages/properties/PropertyDetailsPage/PropertyDetailsPage")
);
const PropertyPreviewPage = lazy(
  () => import("pages/properties/PropertyPreviewPage/PropertyPreviewPage")
);
const PropertyMediaFolderPage = lazy(
  () =>
    import("pages/properties/PropertyMediaFolderPage/PropertyMediaFolderPage")
);
const AmenitiesPage = lazy(() =>
  import("pages/amenities").then((m) => ({ default: m.AmenitiesPage }))
);
const BrokersPage = lazy(() => import("pages/brokers/BrokersPage/BrokersPage"));
const BrokerDetailsPage = lazy(
  () => import("pages/brokers/BrokerDetailsPage/BrokerDetailsPage")
);
const ExternalSaleTeamsPage = lazy(
  () => import("pages/external-sales/external-sale-teams/ExternalSaleTeamsPage")
);
const ExternalSalesUsers = lazy(
  () => import("pages/external-sales/external-sales-users/ExternalSalesUsers")
);
const ManagementPage = lazy(() => import("pages/management/ManagementPage"));
const AccountPage = lazy(() =>
  import("pages/account").then((m) => ({ default: m.AccountPage }))
);
const PrivacyPolicy = lazy(
  () => import("pages/flow/PrivacyPolicy/PrivacyPolicy")
);
const TermConditions = lazy(
  () => import("pages/flow/TermConditions/TermConditions")
);
const MarketAreasPage = lazy(() =>
  import("pages/market-areas").then((m) => ({ default: m.MarketAreasPage }))
);
const SignInPage = lazy(() =>
  import("v2/pages/sign-in-page").then((m) => ({ default: m.SignInPage }))
);
const MfaConfirmationPage = lazy(() =>
  import("v2/pages/mfa-confirmation-page").then((m) => ({
    default: m.MfaConfirmationPage,
  }))
);
const ApiManagementPage = lazy(
  () => import("pages/api-management/ApiManagementPage")
);
const ApiCompanyDetailsPage = lazy(
  () =>
    import("pages/api-management/ApiCompanyDetailsPage/ApiCompanyDetailsPage")
);
const ApiPluginUserDetailsPage = lazy(
  () =>
    import(
      "pages/api-management/ApiPluginUserDetailsPage/ApiPluginUserDetailsPage"
    )
);
const ApiConsolePage = lazy(() =>
  import("pages/api-console").then((m) => ({ default: m.ApiConsolePage }))
);
const ApiConsoleAccountDetailsPage = lazy(() =>
  import("pages/api-console-account-details").then((m) => ({
    default: m.ApiConsoleAccountDetailsPage,
  }))
);
const ApiConsolePluginUserDetailsPage = lazy(() =>
  import("pages/api-console-plugin-user-details").then((m) => ({
    default: m.ApiConsolePluginUserDetailsPage,
  }))
);
const ContentActivityPage = lazy(() =>
  import("pages/content-activity").then((m) => ({
    default: m.ContentActivityPage,
  }))
);
const FeaturedPortfoliosPage = lazy(
  () =>
    import(
      "pages/featured-portfolios/FeaturedPortfoliosPage/FeaturedPortfoliosPage"
    )
);
const FeaturedPortfolioDetailsPage = lazy(
  () =>
    import(
      "pages/featured-portfolios/FeaturedPortfolioDetailsPage/FeaturedPortfolioDetailsPage"
    )
);
const FeaturedPropertyDetailsPage = lazy(
  () =>
    import(
      "pages/featured-portfolios/FeaturedPropertyDetailsPage/FeaturedPropertyDetailsPage"
    )
);

function PageFallback() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Spinner />
    </div>
  );
}

function App() {
  const [isViewerReady] = useInitViewer();
  const navigate = useNavigate();
  useBrowserTabTitle();

  return isViewerReady ? (
    <ChunkErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRout>
                <Navigate replace to={RoutesPath.Properties} />
              </ProtectedRout>
            }
          />

          <Route
            path={RoutesPath.Organizations}
            element={
              <ProtectedRout>
                <OrganizationsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.OrganizationDetails}
            element={
              <ProtectedRout>
                <OrganizationDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.Amenities}
            element={
              <ProtectedRout>
                <AmenitiesPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.FeaturedContent}
            element={
              <ProtectedRout stickyBreadcrumbs>
                <FeaturedPortfoliosPage />
              </ProtectedRout>
            }
          />

          <Route
            path={RoutesPath.FeaturedContent}
            element={
              <ProtectedRout stickyBreadcrumbs>
                <FeaturedPortfoliosPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.FeaturedContentPortfoliosDetails}
            element={
              <ProtectedRout stickyBreadcrumbs>
                <FeaturedPortfolioDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.FeaturedContentPropertiesDetails}
            element={
              <ProtectedRout stickyBreadcrumbs>
                <FeaturedPropertyDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.Properties}
            element={
              <ProtectedRout>
                <PropertiesPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.ExternalSales}
            element={
              <ProtectedRout>
                <ExternalSaleTeamsPage />
              </ProtectedRout>
            }
          />

          <Route
            path={RoutesPath.ExternalSalesUsers}
            element={
              <ProtectedRout>
                <ExternalSalesUsers />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.PropertyDetails}
            element={
              <ProtectedRout stickyBreadcrumbs>
                <PropertyDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.PropertyAgentPreview}
            element={
              <ProtectedRout withContainer={false}>
                <PropertyPreviewPage isSeeker={false} />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.PropertyDirectPreview}
            element={
              <ProtectedRout withContainer={false}>
                <PropertyPreviewPage isSeeker={true} />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.PropertyMediaFolder}
            element={
              <ProtectedRout stickyBreadcrumbs>
                <PropertyMediaFolderPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.Brokers}
            element={
              <ProtectedRout>
                <BrokersPage isSeeker={false} />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.BrokersDetails}
            element={
              <ProtectedRout>
                <BrokerDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.Seekers}
            element={
              <ProtectedRout>
                <BrokersPage isSeeker={true} />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.SeekersDetails}
            element={
              <ProtectedRout>
                <BrokerDetailsPage />
              </ProtectedRout>
            }
          />
          {/* <Route path={RoutesPath.SignIn} element={<SignIn />} /> */}
          <Route path={RoutesPath.SignIn} element={<SignInPage />} />
          <Route
            path={RoutesPath.SignInMFA}
            element={<MfaConfirmationPage />}
          />
          <Route
            path={RoutesPath.ForgotPassword}
            element={<ForgotPasswordPage />}
          />
          <Route
            path={RoutesPath.ResetPassword}
            element={<ResetPasswordPage />}
          />
          <Route
            path={RoutesPath.Management}
            element={
              <ProtectedRout>
                <ManagementPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.APIManagement}
            element={
              <ProtectedRout>
                <ApiManagementPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.APIManagementCompanyDetails}
            element={
              <ProtectedRout>
                <ApiCompanyDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.APIManagementPluginUserDetails}
            element={
              <ProtectedRout>
                <ApiPluginUserDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.APIConsole}
            element={
              <ProtectedRout>
                <ApiConsolePage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.APIConsoleAccountDetails}
            element={
              <ProtectedRout>
                <ApiConsoleAccountDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.APIConsolePluginUserDetails}
            element={
              <ProtectedRout>
                <ApiConsolePluginUserDetailsPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.ContentActivity}
            element={
              <ProtectedRout>
                <ContentActivityPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.Account}
            element={
              <ProtectedRout>
                <AccountPage />
              </ProtectedRout>
            }
          />
          <Route
            path={RoutesPath.MarketAreas}
            element={
              <ProtectedRout>
                <MarketAreasPage />
              </ProtectedRout>
            }
          />

          <Route
            path={RoutesPath.TermConditions}
            element={<TermConditions />}
          />
          <Route path={RoutesPath.PrivacyPolicy} element={<PrivacyPolicy />} />

          <Route
            path="*"
            element={
              <NotFoundPage
                onRedirect={() => navigate(RoutesPath.Properties)}
              />
            }
          />
        </Routes>
      </Suspense>
    </ChunkErrorBoundary>
  ) : null;
}

export default App;
